import React from 'react';
import { History, Play, Download, ChevronRight } from 'lucide-react';
const agents = [{
  id: '1',
  name: 'Private Equity & Ownership',
  riskLevel: 'Low',
  riskColor: 'green',
  summary: 'Still family-owned since 1971. Willy Wonka holds majority stake, Charlie Bucket has minority ownership. No recent changes or private equity involvement detected.',
  lastRun: 'May 14, 2026 3:56 PM'
}, {
  id: '2',
  name: 'Customer Reviews & Sentiment',
  riskLevel: 'Medium',
  riskColor: 'yellow',
  summary: 'Mixed reviews at 3.4/5 stars. Complaints about delayed shipments and quality issues up 23% this quarter. Golden Ticket fans still love the brand, but newer customers cite communication problems.',
  lastRun: 'May 14, 2026 1:33 PM'
}, {
  id: '3',
  name: 'Legal, Litigation & Public Records',
  riskLevel: 'High',
  riskColor: 'red',
  summary: 'Active lawsuit from Slugworth Confections seeking $340K for breach of distribution contract. Three OSHA workplace safety citations issued in March 2026.',
  lastRun: 'May 13, 2026 2:15 PM'
}];
function RiskBadge({
  level,
  color
}: {
  level: string;
  color: string;
}) {
  const bgColor = color === 'green' ? 'bg-green-100 text-green-800 border-green-300' : color === 'yellow' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' : 'bg-red-100 text-red-800 border-red-300';
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${bgColor}`} data-id="element-4297">
      {level}
    </span>;
}
export function WonkaResearchAgentsTab() {
  return <div className="space-y-6" data-id="element-4298">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4" data-id="element-4299">
        <div className="flex items-center justify-between" data-id="element-4300">
          <p className="text-sm text-gray-600" data-id="element-4301">
            Click Run All to start the agents, or run them individually.
          </p>
          <div className="flex items-center gap-3" data-id="element-4302">
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-4303">
              <History className="w-4 h-4" data-id="element-4304" />
              HISTORY
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold transition-colors" data-id="element-4305">
              <Play className="w-4 h-4" data-id="element-4306" />
              RUN ALL
            </button>
          </div>
        </div>
      </div>

      {/* Risk Summary */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-4307">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between" data-id="element-4308">
          <div className="flex items-center gap-3" data-id="element-4309">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-4310">Risk Summary</h2>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300" data-id="element-4311">
              Medium
            </span>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-4312">
            <Download className="w-4 h-4" data-id="element-4313" />
            EXPORT CSV
          </button>
        </div>

        {/* Agents Table */}
        <div className="overflow-x-auto" data-id="element-4314">
          <table className="w-full text-left border-collapse" data-id="element-4315">
            <thead data-id="element-4316">
              <tr className="bg-gray-50 border-b border-gray-200" data-id="element-4317">
                <th className="px-6 py-3 text-sm font-semibold text-gray-600 w-1/5" data-id="element-4318">
                  Agent
                </th>
                <th className="px-6 py-3 text-sm font-semibold text-gray-600 w-32 text-center" data-id="element-4319">
                  Risk Level
                </th>
                <th className="px-6 py-3 text-sm font-semibold text-gray-600" data-id="element-4320">
                  Summary
                </th>
                <th className="px-6 py-3 text-sm font-semibold text-gray-600 w-48 text-center" data-id="element-4321">
                  Last Run
                </th>
                <th className="px-6 py-3 w-12" data-id="element-4322"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-4323">
              {agents.map(agent => <tr key={agent.id} className="hover:bg-gray-50/50 transition-colors" data-id="element-4324">
                  <td className="px-6 py-4" data-id="element-4325">
                    <span className="font-semibold text-gray-900" data-id="element-4326">
                      {agent.name}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center" data-id="element-4327">
                    <div className="flex justify-center" data-id="element-4328">
                      <RiskBadge level={agent.riskLevel} color={agent.riskColor} data-id="element-4329" />
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-4330">
                    <p className="text-sm text-gray-700 leading-relaxed" data-id="element-4331">
                      {agent.summary}
                    </p>
                  </td>
                  <td className="px-6 py-4" data-id="element-4332">
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-600" data-id="element-4333">
                      <span className="w-2 h-2 rounded-full bg-green-500" data-id="element-4334"></span>
                      {agent.lastRun}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-4335">
                    <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-4336">
                      <ChevronRight className="w-5 h-5 text-gray-400" data-id="element-4337" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}